import React from 'react';
import ChakraIcon from '../components/common/ChakraIcon';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Stack,
  Icon,
  Button,
  Divider,
  VStack,
  HStack,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Select,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaHandHoldingHeart, 
  FaHandsHelping, 
  FaHandshake, 
  FaMoneyBillWave,
  FaUsers,
  FaLaptop,
  FaBriefcase,
  FaGraduationCap
} from 'react-icons/fa';


// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const GetInvolved: React.FC = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };
  
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Ways to get involved
  const involvementWays = [
    {
      title: 'Donate',
      description: 'Support our work financially to help us reach more communities.',
      icon: FaMoneyBillWave,
      color: 'brand.500',
      action: 'Donate Now'
    },
    {
      title: 'Volunteer',
      description: 'Share your time and skills to make a difference on the ground.',
      icon: FaHandsHelping,
      color: 'green.500',
      action: 'Apply Now'
    },
    {
      title: 'Partner',
      description: 'Collaborate with us to create greater impact through joint initiatives.',
      icon: FaHandshake,
      color: 'earth.500',
      action: 'Become a Partner'
    }
  ];

  // Volunteer opportunities
  const volunteerOpportunities = [
    {
      title: 'Community Outreach',
      description: 'Engage with local communities to assess needs and build relationships.',
      icon: FaUsers,
      commitment: 'Flexible, 5-10 hours/week'
    },
    {
      title: 'Digital Support',
      description: 'Help with website maintenance, social media, and digital content creation.',
      icon: FaLaptop,
      commitment: 'Remote, 3-5 hours/week'
    },
    {
      title: 'Professional Services',
      description: 'Provide specialized skills in areas like healthcare, education, or construction.',
      icon: FaBriefcase,
      commitment: 'Project-based, varies'
    },
    {
      title: 'Training & Education',
      description: 'Facilitate workshops and training sessions in your area of expertise.',
      icon: FaGraduationCap,
      commitment: 'Periodic, 2-3 days per month'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        h={{ base: '40vh', md: '50vh' }}
        bgImage="linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/assets/images/photo_2025-05-04_15-32-38.jpg')"
        bgPosition="center"
        bgSize="cover"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="container.xl" textAlign="center">
          <MotionHeading
            as={motion.h1}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="bold"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            Get Involved
          </MotionHeading>
          
          <MotionText
            fontSize={{ base: 'lg', md: 'xl' }}
            mt={4}
            maxW="800px"
            mx="auto"
            initial="hidden"
            animate="visible"
            variants={slideUp}
          >
            Join us in our mission to foster life, peace, and happiness in communities across the Democratic Republic of Congo.
          </MotionText>
        </Container>
      </Box>

      {/* Ways to Get Involved */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center" mb={16}>
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Ways to Get Involved
              </Heading>
              
              <Divider w="100px" borderColor="brand.500" borderWidth="2px" mb={6} />
              
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                mb={6}
              >
                There are many ways you can support ALPHA's mission and make a difference in the lives of people in the Democratic Republic of Congo.
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }}>
                Whether through volunteering, donations, or partnerships, your contribution can help us create lasting positive change in communities across the region.
              </Text>
            </MotionBox>
            
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideUp}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-34-12.jpg"
                alt="Community Involvement"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </MotionBox>
          </SimpleGrid>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              {involvementWays.map((way, index) => (
                <MotionBox
                  key={index}
                  p={8}
                  borderRadius="lg"
                  boxShadow="md"
                  bg="white"
                  textAlign="center"
                  sx={{ transition: 'all 0.3s ease' }}
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
                  variants={slideUp}
                >
                  <Flex
                    w="80px"
                    h="80px"
                    borderRadius="full"
                    bg={`${way.color}10`}
                    color={way.color}
                    justify="center"
                    align="center"
                    mx="auto"
                    mb={6}
                  >
                    <ChakraIcon icon={way.icon} boxSize="40px" />
                  </Flex>
                  
                  <Heading as="h3" fontSize="xl" mb={4}>
                    {way.title}
                  </Heading>
                  
                  <Text color="gray.600" mb={6}>
                    {way.description}
                  </Text>
                  
                  <Button
                    variant="solid"
                    bg={way.color}
                    color="white"
                    size="md"
                    _hover={{
                      bg: `${way.color.split('.')[0]}.600`,
                    }}
                  >
                    {way.action}
                  </Button>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionFlex>
        </Container>
      </Box>

      {/* Donation Section */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center" mb={16}>
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideUp}
              order={{ base: 2, md: 1 }}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-34-25.jpg"
                alt="Support Our Work"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </MotionBox>
            
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              order={{ base: 1, md: 2 }}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Support Our Work
              </Heading>
              
              <Divider w="100px" borderColor="brand.500" borderWidth="2px" mb={6} />
              
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                mb={6}
              >
                Your financial contribution helps us continue our vital work in communities across the Democratic Republic of Congo.
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }}>
                Every donation, no matter the size, makes a significant impact in our ability to implement programs and support communities in need.
              </Text>
            </MotionBox>
          </SimpleGrid>
          
          <MotionBox
            maxW="800px"
            mx="auto"
            p={8}
            borderRadius="lg"
            boxShadow="md"
            bg="white"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideUp}
          >
            <VStack align="flex-start" spacing={4} mb={8}>
              <Box>
                <Heading as="h3" fontSize="lg" mb={2} color="brand.500">
                  Educational programs and training materials
                </Heading>
                <Text>Help us provide educational resources and training to communities in need.</Text>
              </Box>
              
              <Box>
                <Heading as="h3" fontSize="lg" mb={2} color="brand.500">
                  Community development initiatives
                </Heading>
                <Text>Support our community development initiatives, including infrastructure projects and economic empowerment programs.</Text>
              </Box>
              
              <Box>
                <Heading as="h3" fontSize="lg" mb={2} color="brand.500">
                  Emergency relief for vulnerable populations
                </Heading>
                <Text>Help us provide emergency relief to vulnerable populations, including refugees and internally displaced persons.</Text>
              </Box>
              
              <Box>
                <Heading as="h3" fontSize="lg" mb={2} color="brand.500">
                  Mental health and wellbeing programs
                </Heading>
                <Text>Support our mental health and wellbeing programs, including counseling services and trauma support.</Text>
              </Box>
              
              <Box>
                <Heading as="h3" fontSize="lg" mb={2} color="brand.500">
                  Operational costs to sustain our work
                </Heading>
                <Text>Help us cover operational costs, including staff salaries, office expenses, and program administration.</Text>
              </Box>
            </VStack>
            
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
              <Button
                variant="primary"
                size="lg"
              >
                One-Time Donation
              </Button>
              <Button
                variant="outline"
                colorScheme="blue"
                size="lg"
              >
                Monthly Giving
              </Button>
            </Stack>
          </MotionBox>
        </Container>
      </Box>

      {/* Volunteer Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center" mb={16}>
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideUp}
              order={{ base: 2, md: 1 }}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-34-45.jpg"
                alt="Volunteer Opportunities"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </MotionBox>
            
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              order={{ base: 1, md: 2 }}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Volunteer Opportunities
              </Heading>
              
              <Divider w="100px" borderColor="green.500" borderWidth="2px" mb={6} />
              
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                mb={6}
              >
                Share your time and skills to make a difference in the lives of people in the Democratic Republic of Congo.
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }}>
                Whether you're a student, professional, or simply someone who wants to make a difference, we have volunteer opportunities for you.
              </Text>
            </MotionBox>
          </SimpleGrid>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
            {volunteerOpportunities.map((opportunity, index) => (
              <Box
                key={index}
                p={4}
                borderRadius="md"
                bg="gray.50"
                sx={{ transition: 'all 0.3s ease' }}
                _hover={{ transform: 'translateY(-5px)', shadow: 'md' }}
              >
                <HStack spacing={4} align="flex-start">
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="green.50"
                    color="green.500"
                    justify="center"
                    align="center"
                    mt={1}
                  >
                    <ChakraIcon icon={opportunity.icon} boxSize="20px" />
                  </Flex>
                  
                  <VStack align="flex-start" spacing={1}>
                    <Heading as="h3" fontSize="md">
                      {opportunity.title}
                    </Heading>
                    
                    <Text fontSize="sm" color="gray.600">
                      {opportunity.description}
                    </Text>
                    
                    <Text fontSize="xs" fontWeight="bold" color="green.500" mt={1}>
                      {opportunity.commitment}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
          
          <Button
            variant="solid"
            bg="green.500"
            color="white"
            size="lg"
            _hover={{
              bg: 'green.600',
            }}
          >
            Apply to Volunteer
          </Button>
        </Container>
      </Box>

      {/* Partnership Section */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center" mb={16}>
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideUp}
              order={{ base: 2, md: 1 }}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-34-48.jpg"
                alt="Partnership Opportunities"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </MotionBox>
            
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              order={{ base: 1, md: 2 }}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Partnership Opportunities
              </Heading>
              
              <Divider w="100px" borderColor="earth.500" borderWidth="2px" mb={6} />
              
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                mb={6}
              >
                Collaborate with us to create greater impact through joint initiatives.
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }}>
                Whether you're a business, organization, or individual, we invite you to partner with us to make a difference in the lives of people in the Democratic Republic of Congo.
              </Text>
            </MotionBox>
          </SimpleGrid>
          
          <VStack align="flex-start" spacing={4} mb={8}>
            <Box>
              <Heading as="h3" fontSize="lg" mb={2} color="earth.500">
                Implementing Partners
              </Heading>
              <Text>Organizations that work with us to implement programs and projects on the ground.</Text>
            </Box>
            
            <Box>
              <Heading as="h3" fontSize="lg" mb={2} color="earth.500">
                Technical Partners
              </Heading>
              <Text>Organizations that provide specialized expertise and technical support to enhance our work.</Text>
            </Box>
            
            <Box>
              <Heading as="h3" fontSize="lg" mb={2} color="earth.500">
                Funding Partners
              </Heading>
              <Text>Organizations that provide financial support for our programs and operations.</Text>
            </Box>
            
            <Box>
              <Heading as="h3" fontSize="lg" mb={2} color="earth.500">
                Research Partners
              </Heading>
              <Text>Academic and research institutions that collaborate on studies and knowledge generation.</Text>
            </Box>
          </VStack>
          
          <Button
            variant="solid"
            bg="earth.500"
            color="white"
            size="lg"
            _hover={{
              bg: 'earth.600',
            }}
          >
            Explore Partnership
          </Button>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={12}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Get in Touch
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              Have questions about how to get involved? Fill out the form below and we'll get back to you.
            </Text>
          </MotionBox>
          
          <MotionBox
            maxW="800px"
            mx="auto"
            p={8}
            borderRadius="lg"
            boxShadow="md"
            bg="white"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideUp}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
              <FormControl isRequired>
                <FormLabel>First Name</FormLabel>
                <Input placeholder="Your first name" />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Last Name</FormLabel>
                <Input placeholder="Your last name" />
              </FormControl>
            </SimpleGrid>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input placeholder="Your email address" type="email" />
              </FormControl>
              
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input placeholder="Your phone number (optional)" />
              </FormControl>
            </SimpleGrid>
            
            <FormControl mb={6} isRequired>
              <FormLabel>I'm interested in:</FormLabel>
              <Select placeholder="Select an option">
                <option value="donate">Making a donation</option>
                <option value="volunteer">Volunteering</option>
                <option value="partner">Partnership opportunities</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>
            
            <FormControl mb={6} isRequired>
              <FormLabel>Message</FormLabel>
              <Textarea placeholder="Tell us how you'd like to get involved" rows={5} />
            </FormControl>
            
            <Button
              variant="primary"
              size="lg"
              w="full"
            >
              Submit
            </Button>
          </MotionBox>
        </Container>
      </Box>
    </Box>
  );
};

export default GetInvolved;
